import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Grid from '@/components/molecules/Grid';
import { ModalTitle } from '@/components/atoms/UI/Modal/styles';
import RatingSelector from '@/components/features/RatingSelector/RatingSelector';
import { useDispatch, useSelector } from 'react-redux';
import Textarea from '@/components/atoms/UI/forms/Textarea/Textarea';
import TextField from '@/components/atoms/UI/forms/TextField/TextField';
import SuccessMessage from '@/components/atoms/UI/forms/SuccessMessage/SuccessMessage';
import Button from '@/components/atoms/UI/buttons/Button';
import { useTranslation } from 'react-i18next';
import ErrorMessage from '@/components/atoms/UI/forms/ErrorMessage/ErrorMessage';
import schemaTestRatingForm from '@/core/schemes/schemaTestRatingForm.js';
import { sendTestRating, setRatingFormEmail } from '@/store/reducers/testRatingFormSlice';
import { selectSelectedRating } from '@/store/selectors/ratingSelectors';
import DottedLink from '@/components/atoms/UI/links/DottedLink';
import { TestRatingFormTypes } from '@/store/types/testRatingFormTypes';
import { AppDispatch } from '@/store/store';
import { selectAttemptKey } from '@/store/selectors/selectAttemptKey';
import { selectTestId } from '@/store/selectors/testIdSelector';
import { selectUserEmail } from '@/store/selectors/resultSelectors';
import { setIsRatingSaved } from '@/store/reducers/testRatingSlice';


const TestRatingForm: React.FC<{ onClose: () => void }> = () => {
	const [isReportFormVisible, setReportFormVisible] = useState(false);
	const [showOnlySuccessMessage, setShowOnlySuccessMessage] = useState(false);

	const dispatch: AppDispatch = useDispatch();

	const ratingFormData = useSelector((state: any) => state.testRatingForm.data);
	const error = useSelector((state: any) => state.testRatingForm.error);
	const successMessage = useSelector((state: any) => state.testRatingForm.successMessage);
	const selectedRating = useSelector(selectSelectedRating);
	const attemptKey = useSelector(selectAttemptKey);
	const testId = useSelector(selectTestId);
	const userEmail = useSelector(selectUserEmail);


	const { t } = useTranslation();

	const text_title = t('testRatingForm.title');
	const text_report_error = t('testRatingForm.repost_error');
	const text_comment = t('testRatingForm.comment');
	const text_email = t('testRatingForm.email');
	const text_send = t('testRatingForm.send');

	const {
		register,
		handleSubmit,
		formState,
		reset,
		formState: { errors, isValid }
	} = useForm<TestRatingFormTypes>({
		defaultValues: ratingFormData,
		mode: 'onTouched',
		resolver: yupResolver(schemaTestRatingForm) as any,
	})

	useEffect(() => {
		if (successMessage) {
			reset(ratingFormData);
			setShowOnlySuccessMessage(true);
			dispatch(setIsRatingSaved(true));
		}
	}, [successMessage, reset, ratingFormData]);

	const onSubmit = (data: TestRatingFormTypes) => {
		const formData = {
			...data,
			test_id: testId,
			attempt_key: attemptKey,
			rating: selectedRating,
		};
		dispatch(sendTestRating(formData));
	}

	return (
		<Grid col='1' gap='3' centeredContent>
			{showOnlySuccessMessage ? (
				<b>{successMessage}</b>
			) : (
				<>
					<ModalTitle>{text_title}</ModalTitle>
					<RatingSelector autoWidth />

					{!isReportFormVisible && (
						<DottedLink onClick={() => setReportFormVisible(true)}>
							{text_report_error}
						</DottedLink>
					)}

					<form onSubmit={handleSubmit(onSubmit)}>
						<Grid col='1' gap='2' colDesktop='1' centeredContent>
							{isReportFormVisible && (
								<>
									<Textarea
										name={'msg'}
										placeholder={text_comment}
										register={register}
										type='textarea'
										error={errors.msg?.message}
									/>

									<TextField
										name={'email'}
										value={userEmail}
										placeholder={text_email}
										register={register}
										type='text'
										error={errors.email?.message}
									/>
								</>
							)}

							{successMessage && <SuccessMessage>{successMessage}</SuccessMessage>}
							{error && <ErrorMessage>{error}</ErrorMessage>}

							<Button type='submit' disabled={selectedRating <= 0 || !isValid}>
								{text_send}
							</Button>
						</Grid>
					</form>
				</>
			)}
		</Grid>
	);
};

export default TestRatingForm